import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTestEvaluation } from '../hooks/useTestEvaluation';
import { TestResult, ReviewerComment } from '../types/testEvaluation';
import { 
  CheckCircle2, 
  XCircle, 
  ArrowLeft,
  AlertCircle,
  Clock,
  User,
  MessageSquare,
  ChevronRight
} from 'lucide-react';

const TestResultsBrowse: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    results,
    loading,
    error,
    fetchResults,
    addComment,
    deleteComment,
    hasAccess,
    clearError,
    loadMore,
    continuationToken,
  } = useTestEvaluation();

  const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
  const [activeTab, setActiveTab] = useState<'conversation' | 'scores' | 'comments' | 'metadata'>('conversation');
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Filter state
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedAgentVersion, setSelectedAgentVersion] = useState<string>('');
  const [showWithComments, setShowWithComments] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  // Get unique categories from results
  const categories = React.useMemo(() => {
    const cats = new Set<string>();
    results.forEach(r => {
      if (r.category) cats.add(r.category);
    });
    return Array.from(cats).sort();
  }, [results]);

  // Get unique agent versions from results (format: YYYYMMDD-versionHash) with counts
  const agentVersions = React.useMemo(() => {
    const versionCounts = new Map<string, number>();
    results.forEach(r => {
      if (r.agentVersion?.versionHash && r.timestamp) {
        // Extract date from timestamp (YYYYMMDD format)
        const date = r.timestamp.substring(0, 10).replace(/-/g, '');
        const versionKey = `${date}-${r.agentVersion.versionHash}`;
        versionCounts.set(versionKey, (versionCounts.get(versionKey) || 0) + 1);
      }
    });
    return Array.from(versionCounts.entries())
      .sort((a, b) => b[0].localeCompare(a[0])) // Most recent first
      .map(([version, count]) => ({ version, count }));
  }, [results]);

  // Filter results client-side for comments and agent version
  const filteredResults = React.useMemo(() => {
    let filtered = results;
    
    // Filter by comments
    if (showWithComments) {
      filtered = filtered.filter(r => r.reviewerComments && r.reviewerComments.length > 0);
    }
    
    // Filter by agent version (client-side)
    if (selectedAgentVersion) {
      filtered = filtered.filter(r => {
        if (!r.agentVersion?.versionHash || !r.timestamp) return false;
        const date = r.timestamp.substring(0, 10).replace(/-/g, '');
        const versionKey = `${date}-${r.agentVersion.versionHash}`;
        return versionKey === selectedAgentVersion;
      });
    }
    
    return filtered;
  }, [results, showWithComments, selectedAgentVersion]);

  // Fetch initial results on mount
  useEffect(() => {
    if (hasAccess) {
      fetchResults({ maxItems: 50 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  // Auto-refresh when filters change
  useEffect(() => {
    if (!hasAccess) return;

    const params: any = { maxItems: 50 };
    
    if (selectedCategory) {
      params.category = selectedCategory;
    }
    if (selectedStatus !== 'all') {
      params.passed = selectedStatus === 'passed';
    }
    if (dateRange.startDate) {
      params.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      params.endDate = dateRange.endDate;
    }

    fetchResults(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedStatus, dateRange.startDate, dateRange.endDate, hasAccess]);

  // Select result from URL parameter or first result
  useEffect(() => {
    if (filteredResults.length > 0) {
      if (id) {
        const result = filteredResults.find(r => r.id === id);
        if (result) {
          setSelectedResult(result);
        } else {
          // If ID not found in current results, select first one
          setSelectedResult(filteredResults[0]);
          navigate(`/test-evaluation/browse/${filteredResults[0].id}`, { replace: true });
        }
      } else {
        // No ID in URL, select first result
        setSelectedResult(filteredResults[0]);
        navigate(`/test-evaluation/browse/${filteredResults[0].id}`, { replace: true });
      }
    }
  }, [id, filteredResults, navigate]);

  // Update selected result when it changes in the results array (e.g., after adding comment)
  useEffect(() => {
    if (selectedResult && results.length > 0) {
      const updatedResult = results.find(r => r.id === selectedResult.id);
      if (updatedResult) {
        setSelectedResult(updatedResult);
      }
    }
  }, [results, selectedResult]);

  const handleSelectResult = (result: TestResult) => {
    setSelectedResult(result);
    navigate(`/test-evaluation/browse/${result.id}`);
    setActiveTab('conversation');
  };

  const handleAddComment = async () => {
    if (!selectedResult || !newComment.trim()) return;

    setSubmittingComment(true);
    try {
      await addComment(selectedResult.id, newComment.trim());
      setNewComment('');
    } catch (err) {
      // Error is handled by the hook
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedResult) return;

    try {
      await deleteComment(selectedResult.id, commentId);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleLoadMore = () => {
    if (continuationToken && !loading) {
      loadMore();
    }
  };

  if (!hasAccess) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-rm-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-rm-ink-900 mb-2">Access Denied</h2>
          <p className="text-rm-ink-600">You need the testContributor role to access this page.</p>
        </div>
      </div>
    );
  }

  const handleClearFilters = () => {
    setSelectedCategory('');
    setSelectedStatus('all');
    setSelectedAgentVersion('');
    setShowWithComments(false);
    setDateRange({ startDate: '', endDate: '' });
  };

  return (
    <div className="h-full flex flex-col py-6">
      {/* Header */}
      <div className="flex-shrink-0 mb-6">
        <button
          onClick={() => navigate('/test-evaluation')}
          className="flex items-center text-rm-primary-600 hover:text-rm-primary-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>
        
        {/* Header with Compact Filters */}
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold text-rm-ink-900">Browse Test Results</h1>
          
          {/* Compact Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
            >
              <option value="all">All</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={selectedAgentVersion}
              onChange={(e) => setSelectedAgentVersion(e.target.value)}
              className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
              title="Filter by run date and agent version"
            >
              <option value="">All Versions</option>
              {agentVersions.map(({ version, count }) => (
                <option key={version} value={version}>
                  {version} ({count})
                </option>
              ))}
            </select>

            <label className="flex items-center gap-2 text-sm px-2 py-1 border border-rm-ink-300 rounded-md cursor-pointer hover:bg-rm-ink-50 transition-colors">
              <input
                type="checkbox"
                checked={showWithComments}
                onChange={(e) => setShowWithComments(e.target.checked)}
                className="rounded border-rm-ink-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <span className="whitespace-nowrap">With Comments</span>
            </label>

            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              placeholder="Start Date"
              className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
            />

            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              placeholder="End Date"
              className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
            />

            {(selectedCategory || selectedStatus !== 'all' || selectedAgentVersion || showWithComments || dateRange.startDate || dateRange.endDate) && (
              <button
                onClick={handleClearFilters}
                className="text-sm px-2 py-1 bg-rm-ink-100 text-rm-ink-700 rounded-md hover:bg-rm-ink-200 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex-shrink-0 bg-rm-error-50 border border-rm-error-200 rounded-lg p-4 flex items-start mb-4">
          <AlertCircle className="w-5 h-5 text-rm-error-500 mr-3 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-rm-error-700">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-rm-error-500 hover:text-rm-error-700 ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Two-Pane Layout */}
      <div className="flex-1 flex gap-6 min-h-0">
        {/* Left Pane - Results List (30%) */}
        <div className="w-[30%] flex flex-col bg-white border border-rm-ink-200 rounded-lg shadow-card min-h-0">
          <div className="flex-shrink-0 p-4 border-b border-rm-ink-200">
            <h2 className="text-lg font-semibold text-rm-ink-900">Test Results</h2>
            <p className="text-sm text-rm-ink-600 mt-1">
              {showWithComments 
                ? `${filteredResults.length} of ${results.length} with comments` 
                : `${results.length} results loaded`}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {loading && filteredResults.length === 0 ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rm-primary-600 mx-auto"></div>
                <p className="text-rm-ink-600 mt-4">Loading...</p>
              </div>
            ) : filteredResults.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-rm-ink-600">
                  {showWithComments ? 'No test results with comments found.' : 'No test results found.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-rm-ink-200">
                {filteredResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelectResult(result)}
                    className={`w-full p-4 text-left hover:bg-rm-ink-50 transition-colors ${
                      selectedResult?.id === result.id ? 'bg-rm-primary-50 border-l-4 border-rm-primary-600' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          {result.passed ? (
                            <CheckCircle2 className="w-4 h-4 text-rm-success-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-rm-error-500 flex-shrink-0" />
                          )}
                          <span className="text-sm font-medium text-rm-ink-900 truncate">
                            {result.scenarioName || result.scenarioId}
                          </span>
                        </div>
                        <div className="text-xs text-rm-ink-600">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(result.timestamp).toLocaleDateString()}
                            </span>
                            {result.category && (
                              <>
                                <span>•</span>
                                <span className="px-1.5 py-0.5 bg-rm-ink-100 text-rm-ink-700 rounded font-medium">
                                  {result?.scenarioId}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <span>Score: {(result.overallScore * 100).toFixed(0)}%</span>
                            {result.agentVersion && (
                              <>
                                <span>•</span>
                                <span className="font-mono text-rm-ink-500">
                                  v:{result.agentVersion.versionHash.substring(0, 8)}
                                </span>
                              </>
                            )}
                          </div>
                          {result.reviewerComments && result.reviewerComments.length > 0 && (
                            <div className="flex items-center">
                              <MessageSquare className="w-3 h-3 mr-1" />
                              {result.reviewerComments.length} comment{result.reviewerComments.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-rm-ink-400 flex-shrink-0 ml-2" />
                    </div>
                  </button>
                ))}
              </div>
            )}

          </div>

          {/* Load More Button */}
          {continuationToken && (
            <div className="flex-shrink-0 p-4 border-t border-rm-ink-200">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="w-full px-4 py-2 bg-rm-primary-600 text-white rounded-md hover:bg-rm-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>

        {/* Right Pane - Test Result Details (70%) */}
        <div className="flex-1 flex flex-col bg-white border border-rm-ink-200 rounded-lg shadow-card min-h-0">
          {selectedResult ? (
            <>
              {/* Header */}
              <div className="flex-shrink-0 p-6 border-b border-rm-ink-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-rm-ink-900 mb-2">
                      {selectedResult.scenarioName || selectedResult.scenarioId}
                    </h2>
                    <div className="flex items-center space-x-4 text-sm text-rm-ink-600">
                      {selectedResult.category && (
                        <span className="px-2 py-1 bg-rm-ink-100 text-rm-ink-700 rounded">
                          {selectedResult.category}
                        </span>
                      )}
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {new Date(selectedResult.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2 mb-2">
                      {selectedResult.passed ? (
                        <CheckCircle2 className="w-6 h-6 text-rm-success-500" />
                      ) : (
                        <XCircle className="w-6 h-6 text-rm-error-500" />
                      )}
                      <span className={`text-xl font-bold ${selectedResult.passed ? 'text-rm-success-600' : 'text-rm-error-600'}`}>
                        {selectedResult.passed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                    <div className="text-sm text-rm-ink-600">
                      Score: {(selectedResult.overallScore * 100).toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex-shrink-0 border-b border-rm-ink-200 px-6">
                <div className="flex space-x-8">
                  {(['conversation', 'scores', 'comments', 'metadata'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-4 px-2 font-medium text-sm transition-colors ${
                        activeTab === tab
                          ? 'border-b-2 border-rm-primary-600 text-rm-primary-600'
                          : 'text-rm-ink-600 hover:text-rm-ink-900'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      {tab === 'comments' && selectedResult.reviewerComments && selectedResult.reviewerComments.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-rm-primary-100 text-rm-primary-700 text-xs rounded-full">
                          {selectedResult.reviewerComments.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                {activeTab === 'conversation' && (
                  <div>
                    <h3 className="text-lg font-semibold text-rm-ink-900 mb-4">Conversation</h3>
                    {selectedResult.conversationData?.allMessages ? (
                      <div className="space-y-4">
                        {selectedResult.conversationData.allMessages.map((message, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg ${
                              message.role === 'user'
                                ? 'bg-rm-primary-50 ml-8'
                                : 'bg-rm-ink-50 mr-8'
                            }`}
                          >
                            <div className="flex items-center space-x-2 mb-2">
                              <User className="w-4 h-4" />
                              <span className="font-medium text-sm">
                                {message.role === 'user' ? 'User' : 'Assistant'}
                              </span>
                              <span className="text-xs text-rm-ink-600">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-rm-ink-900 whitespace-pre-wrap">{message.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-rm-ink-600">No conversation data available</p>
                    )}
                  </div>
                )}

                {activeTab === 'scores' && (
                  <div>
                    <h3 className="text-lg font-semibold text-rm-ink-900 mb-4">Scores & Validation</h3>
                    
                    {/* Overall Score */}
                    <div className="mb-6 p-4 bg-rm-ink-50 rounded-lg">
                      <div className="text-sm text-rm-ink-600 mb-1">Overall Score</div>
                      <div className="text-3xl font-bold text-rm-ink-900">
                        {(selectedResult.overallScore * 100).toFixed(1)}%
                      </div>
                    </div>

                    {/* Rule Validation */}
                    {selectedResult.ruleValidation && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-rm-ink-900 mb-3">Rule Validation</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between p-3 bg-rm-ink-50 rounded">
                            <span>Status</span>
                            <span className={selectedResult.ruleValidation.passed ? 'text-rm-success-600' : 'text-rm-error-600'}>
                              {selectedResult.ruleValidation.passed ? 'Passed' : 'Failed'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-rm-ink-50 rounded">
                            <span>Score</span>
                            <span>{(selectedResult.ruleValidation.score * 100).toFixed(1)}%</span>
                          </div>
                          {selectedResult.ruleValidation.violations.length > 0 && (
                            <div className="mt-4">
                              <h5 className="font-medium text-rm-error-700 mb-2">Violations</h5>
                              <div className="space-y-2">
                                {selectedResult.ruleValidation.violations.map((violation, idx) => (
                                  <div key={idx} className="p-3 bg-rm-error-50 border border-rm-error-200 rounded">
                                    <div className="font-medium text-rm-error-900">{violation.rule}</div>
                                    <div className="text-sm text-rm-error-700 mt-1">{violation.message}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* AI Evaluation */}
                    {(() => {
                      const aiEval = selectedResult.aiEvaluation;
                      const evaluationData = aiEval?.evaluation || aiEval?.result;

                      if (aiEval?.success && evaluationData) {
                        return (
                          <div className="space-y-6">
                            <div>
                              <h4 className="font-semibold text-rm-ink-900 mb-3 flex items-center justify-between">
                                <span>AI Evaluation</span>
                                {aiEval.model && (
                                  <span className="text-xs font-normal text-rm-ink-600">
                                    Model: {aiEval.model}
                                  </span>
                                )}
                              </h4>

                              {/* Dimension Scores */}
                              {evaluationData.scores && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-rm-ink-700 mb-3">Dimension Scores</h5>
                                  <div className="space-y-2">
                                    {Object.entries(evaluationData.scores).map(([key, value]) => (
                                      value !== undefined && (
                                        <div key={key} className="flex items-center justify-between p-3 bg-rm-ink-50 rounded">
                                          <span className="capitalize text-rm-ink-900">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                          </span>
                                          <div className="flex items-center gap-3">
                                            <div className="w-32 bg-rm-ink-200 rounded-full h-2">
                                              <div 
                                                className="bg-rm-primary-600 h-2 rounded-full transition-all"
                                                style={{ width: `${(value as number) * 100}%` }}
                                              />
                                            </div>
                                            <span className="font-medium text-rm-ink-900 w-12 text-right">
                                              {((value as number) * 100).toFixed(0)}%
                                            </span>
                                          </div>
                                        </div>
                                      )
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Overall AI Score */}
                              {evaluationData.overallScore !== undefined && (
                                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                  <div className="text-sm text-blue-700 mb-1">AI Overall Score</div>
                                  <div className="text-3xl font-bold text-blue-900">
                                    {(evaluationData.overallScore * 100).toFixed(1)}%
                                  </div>
                                </div>
                              )}

                              {/* Reasoning */}
                              {evaluationData.reasoning && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-rm-ink-700 mb-2">Analysis</h5>
                                  <div className="p-4 bg-rm-ink-50 rounded-lg border border-rm-ink-200">
                                    <p className="text-rm-ink-900 whitespace-pre-wrap leading-relaxed">
                                      {evaluationData.reasoning}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Strengths */}
                              {evaluationData.strengths && evaluationData.strengths.length > 0 && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-rm-success-700 mb-2 flex items-center">
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    Strengths
                                  </h5>
                                  <ul className="space-y-2">
                                    {evaluationData.strengths.map((strength: string, idx: number) => (
                                      <li key={idx} className="p-3 bg-rm-success-50 border border-rm-success-200 rounded flex items-start">
                                        <span className="text-rm-success-600 mr-2 mt-0.5">✓</span>
                                        <span className="text-rm-success-900">{strength}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Weaknesses */}
                              {evaluationData.weaknesses && evaluationData.weaknesses.length > 0 && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-amber-700 mb-2 flex items-center">
                                    <AlertCircle className="w-4 h-4 mr-2" />
                                    Areas for Improvement
                                  </h5>
                                  <ul className="space-y-2">
                                    {evaluationData.weaknesses.map((weakness: string, idx: number) => (
                                      <li key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded flex items-start">
                                        <span className="text-amber-600 mr-2 mt-0.5">!</span>
                                        <span className="text-amber-900">{weakness}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Protocol Analysis */}
                              {evaluationData.protocolAnalysis && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-rm-ink-700 mb-2">Protocol Analysis</h5>
                                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <div className="space-y-3">
                                      {Object.entries(evaluationData.protocolAnalysis).map(([key, value]) => (
                                        <div key={key} className="flex flex-col">
                                          <span className="text-xs font-medium text-purple-700 uppercase tracking-wide mb-1">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                          </span>
                                          <div className="text-sm text-purple-900">
                                            {Array.isArray(value) ? (
                                              <ul className="list-disc list-inside space-y-1">
                                                {value.map((item: any, idx: number) => (
                                                  <li key={idx}>{String(item)}</li>
                                                ))}
                                              </ul>
                                            ) : typeof value === 'boolean' ? (
                                              <span className={value ? 'text-rm-success-700 font-medium' : 'text-rm-error-700 font-medium'}>
                                                {value ? '✓ Yes' : '✗ No'}
                                              </span>
                                            ) : (
                                              <span>{String(value)}</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Resource Analysis */}
                              {evaluationData.resourceAnalysis && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-rm-ink-700 mb-2">Resource Analysis</h5>
                                  <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                                    <div className="space-y-3">
                                      {Object.entries(evaluationData.resourceAnalysis).map(([key, value]) => (
                                        <div key={key} className="flex flex-col">
                                          <span className="text-xs font-medium text-teal-700 uppercase tracking-wide mb-1">
                                            {key.replace(/([A-Z])/g, ' $1').trim()}
                                          </span>
                                          <div className="text-sm text-teal-900">
                                            {Array.isArray(value) ? (
                                              <ul className="list-disc list-inside space-y-1">
                                                {value.map((item: any, idx: number) => (
                                                  <li key={idx}>{String(item)}</li>
                                                ))}
                                              </ul>
                                            ) : typeof value === 'boolean' ? (
                                              <span className={value ? 'text-rm-success-700 font-medium' : 'text-rm-error-700 font-medium'}>
                                                {value ? '✓ Yes' : '✗ No'}
                                              </span>
                                            ) : typeof value === 'number' ? (
                                              <span className="font-medium">{value}</span>
                                            ) : (
                                              <span>{String(value)}</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Critical Issues */}
                              {evaluationData.criticalIssues && evaluationData.criticalIssues.length > 0 && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-rm-error-700 mb-2 flex items-center">
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Critical Issues
                                  </h5>
                                  <ul className="space-y-2">
                                    {evaluationData.criticalIssues.map((issue: string, idx: number) => (
                                      <li key={idx} className="p-3 bg-rm-error-50 border border-rm-error-200 rounded flex items-start">
                                        <span className="text-rm-error-600 mr-2 mt-0.5">✕</span>
                                        <span className="text-rm-error-900">{issue}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Recommendations */}
                              {evaluationData.recommendations && evaluationData.recommendations.length > 0 && (
                                <div className="mb-6">
                                  <h5 className="text-sm font-medium text-blue-700 mb-2 flex items-center">
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Recommendations
                                  </h5>
                                  <ul className="space-y-2">
                                    {evaluationData.recommendations.map((rec: string, idx: number) => (
                                      <li key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded flex items-start">
                                        <span className="text-blue-600 mr-2 mt-0.5">→</span>
                                        <span className="text-blue-900">{rec}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // AI Evaluation Failed
                      if (aiEval && !aiEval.success && aiEval.error) {
                        return (
                          <div className="mb-6">
                            <h4 className="font-semibold text-rm-ink-900 mb-3">AI Evaluation</h4>
                            <div className="p-4 bg-rm-error-50 border border-rm-error-200 rounded-lg">
                              <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-rm-error-500 mr-3 mt-0.5 flex-shrink-0" />
                                <div>
                                  <div className="font-medium text-rm-error-900 mb-1">Evaluation Failed</div>
                                  <div className="text-sm text-rm-error-700">{aiEval.error}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // AI Evaluation Skipped
                      if (aiEval?.skipped) {
                        return (
                          <div className="mb-6">
                            <h4 className="font-semibold text-rm-ink-900 mb-3">AI Evaluation</h4>
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                              <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
                                <div>
                                  <div className="font-medium text-amber-900 mb-1">Evaluation Skipped</div>
                                  <div className="text-sm text-amber-700">
                                    {aiEval.reason || 'No reason provided'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })()}
                  </div>
                )}

                {activeTab === 'comments' && (
                  <div>
                    <h3 className="text-lg font-semibold text-rm-ink-900 mb-4">Comments</h3>

                    {/* Add Comment */}
                    <div className="mb-6 p-4 bg-rm-ink-50 rounded-lg">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows={3}
                        className="w-full px-3 py-2 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || submittingComment}
                        className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
                      >
                        {submittingComment ? 'Adding...' : 'Add Comment'}
                      </button>
                    </div>

                    {/* Comments List */}
                    <div className="space-y-4">
                      {selectedResult.reviewerComments && selectedResult.reviewerComments.length > 0 ? (
                        selectedResult.reviewerComments.map((comment: ReviewerComment) => (
                          <div key={comment.id} className="p-4 border border-rm-ink-200 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <div className="font-medium text-rm-ink-900">{comment.displayName}</div>
                                <div className="text-sm text-rm-ink-600">{comment.userEmail}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-rm-ink-600">
                                  {new Date(comment.timestamp).toLocaleString()}
                                </div>
                                {comment.userId === user?.id && (
                                  <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="text-xs text-rm-error-600 hover:text-rm-error-700 mt-1"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-rm-ink-900 whitespace-pre-wrap">{comment.comment}</p>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-rm-ink-600">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No comments yet. Be the first to add one!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'metadata' && (
                  <div>
                    <h3 className="text-lg font-semibold text-rm-ink-900 mb-4">Metadata</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                        <span className="text-rm-ink-600">Document ID</span>
                        <span className="font-mono text-sm">{selectedResult.documentId}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                        <span className="text-rm-ink-600">Conversation ID</span>
                        <span className="font-mono text-sm">{selectedResult.conversationId}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                        <span className="text-rm-ink-600">Agent ID</span>
                        <span className="font-mono text-sm">{selectedResult.agentId}</span>
                      </div>
                      {selectedResult.agentVersion && (
                        <>
                          <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                            <span className="text-rm-ink-600">Agent Version</span>
                            <span className="font-mono text-sm">{selectedResult.agentVersion.versionHash}</span>
                          </div>
                          <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                            <span className="text-rm-ink-600">Version Timestamp</span>
                            <span className="text-sm">{new Date(selectedResult.agentVersion.timestamp).toLocaleString()}</span>
                          </div>
                          {selectedResult.agentVersion.previousVersion && (
                            <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                              <span className="text-rm-ink-600">Previous Version</span>
                              <span className="font-mono text-sm">{selectedResult.agentVersion.previousVersion}</span>
                            </div>
                          )}
                          {selectedResult.agentVersion.changes && selectedResult.agentVersion.changes.length > 0 && (
                            <div className="p-3 bg-rm-ink-50 rounded">
                              <span className="text-rm-ink-600 block mb-2">Changes</span>
                              <div className="space-y-1">
                                {selectedResult.agentVersion.changes.map((change, idx) => (
                                  <div key={idx} className="text-sm">
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs mr-2 ${
                                      change.significance === 'critical' ? 'bg-rm-error-100 text-rm-error-700' :
                                      change.significance === 'high' ? 'bg-orange-100 text-orange-700' :
                                      change.significance === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      {change.significance}
                                    </span>
                                    <span className="font-mono text-xs">{change.field}</span>
                                    {change.type && <span className="text-rm-ink-500 ml-2">({change.type})</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {selectedResult.turnsTotal && (
                        <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                          <span className="text-rm-ink-600">Turns</span>
                          <span>{selectedResult.turnsCompleted}/{selectedResult.turnsTotal}</span>
                        </div>
                      )}
                      {selectedResult.duration && (
                        <div className="flex justify-between p-3 bg-rm-ink-50 rounded">
                          <span className="text-rm-ink-600">Duration</span>
                          <span>{(selectedResult.duration / 1000).toFixed(2)}s</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-rm-ink-600">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a test result to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestResultsBrowse;
